import { useMemo } from 'react';

import { type Booking, type Search } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { type FlightData } from '../../context/useAccommodationContext';
import { compareBetweenTimes } from '../../util/dateUtils';

type ReturnType = {
	flights: Search.OfferItem[];
};

export const useFilteredFlights = (flightAlternatives: FlightData, filters: Booking.FlightFilters): ReturnType => {
	const departureDateOffer: Search.OfferItem | undefined = !flightAlternatives?.loading
		? flightAlternatives?.departureDateOffer
		: undefined;
	const flights: Search.OfferItem[] | undefined = !flightAlternatives?.loading
		? flightAlternatives?.flights
		: undefined;
	const { directOnly, outboundFlight, inboundFlight } = filters;

	return useMemo(() => {
		if (!Array.isArray(flights) || !departureDateOffer) {
			return {
				flights: [],
			};
		}

		let tempFlights = [...flights];

		if (outboundFlight) {
			const outboundFrom = dayjs()
				.set('h', Number(outboundFlight?.from.split(':')[0]))
				.set('m', Number(outboundFlight?.from.split(':')[1]));
			const outboundTo = dayjs()
				.set('h', Number(outboundFlight?.to.split(':')[0]))
				.set('m', Number(outboundFlight?.to.split(':')[1]));
			const hasChanged = outboundFrom.get('hours') !== 0 || outboundTo?.get('hours') !== 0;

			if (hasChanged) {
				tempFlights = tempFlights.filter((flight: Search.OfferItem) =>
					compareBetweenTimes(dayjs(flight.outbound.departureDateTime), outboundFrom, outboundTo),
				);
			}
		}

		if (inboundFlight) {
			const inboundFrom = dayjs()
				.set('h', Number(inboundFlight?.from.split(':')[0]))
				.set('m', Number(inboundFlight?.from.split(':')[1]));
			const inboundTo = dayjs()
				.set('h', Number(inboundFlight?.to.split(':')[0]))
				.set('m', Number(inboundFlight?.to.split(':')[1]));
			const hasChanged = inboundFrom.get('hours') !== 0 || inboundTo?.get('hours') !== 0;

			if (hasChanged) {
				tempFlights = tempFlights.filter((flight: Search.OfferItem) =>
					compareBetweenTimes(dayjs(flight.inbound.departureDateTime), inboundFrom, inboundTo),
				);
			}
		}

		if (directOnly !== undefined) {
			tempFlights = directOnly
				? tempFlights.filter(
						(flight: Search.OfferItem) =>
							flight.inbound.flightSegments?.length !== 2 && flight.outbound.flightSegments?.length !== 2,
					)
				: tempFlights;
		}
		// Sort flights on price
		const flightsSorted = tempFlights.sort((a, b) => a.pricePackage - b.pricePackage);

		return {
			flights: flightsSorted,
		};
	}, [flights, departureDateOffer, outboundFlight, inboundFlight, directOnly]);
};
